import { useCallback, useEffect, useRef, useState, type ReactNode, type UIEvent } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import { PaneContent, PaneFrame, PaneHeaderBar } from "./PaneBlocks";

export interface ListPaneProps {
  readonly title: string;
  readonly count?: number;
  readonly onSearch?: (query: string) => void;
  readonly children: ReactNode;
  readonly headerNode?: ReactNode;
  readonly headerActions?: ReactNode;
  /** Rendered in the search row, attached right of the search input
   *  (e.g. the Chats needs-reply bell). */
  readonly searchAccessory?: ReactNode;
  readonly onContentScroll?: (e: UIEvent<HTMLDivElement>) => void;
}

/**
 * List pane component with header and optional search
 */
export function ListPane({
  title,
  count: _count,
  onSearch,
  children,
  headerNode,
  headerActions,
  searchAccessory,
  onContentScroll,
}: ListPaneProps): JSX.Element {
  const hideScrollbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isScrollbarActive, setIsScrollbarActive] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (hideScrollbarTimerRef.current !== null) {
        clearTimeout(hideScrollbarTimerRef.current);
      }
    };
  }, []);

  const handleListScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    setIsScrollbarActive(true);
    if (hideScrollbarTimerRef.current !== null) {
      clearTimeout(hideScrollbarTimerRef.current);
    }
    hideScrollbarTimerRef.current = setTimeout(() => {
      setIsScrollbarActive(false);
      hideScrollbarTimerRef.current = null;
    }, 1800);
    onContentScroll?.(e);
  }, [onContentScroll]);

  return (
    <PaneFrame tone="surface-secondary">
      <div>
        {headerNode ?? (
          <>
            <PaneHeaderBar
              tone="surface-secondary"
              inset="md"
              withBottomBorder={false}
              className="justify-between gap-3"
            >
              <div className="flex items-center gap-2">
                <h2 className="m-0 text-[15px] font-semibold leading-tight text-content">
                  {title}
                </h2>
              </div>
              <div className="flex items-center gap-2">{headerActions}</div>
            </PaneHeaderBar>
            <div className="px-4 pb-3 flex items-center gap-1.5">
              <div className="flex h-9 flex-1 items-center gap-2 rounded-lg border border-edge bg-surface-tertiary px-3">
                <Icon name="search" size={14} className="text-content-muted" />
              <input
                type="search"
                className="h-full w-full border-none bg-transparent text-[13px] text-content outline-none placeholder:text-content-muted"
                placeholder="Search..."
                aria-label="Search"
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                onChange={onSearch ? (e) => { onSearch(e.target.value); } : undefined}
                readOnly={!onSearch}
              />
              </div>
              {searchAccessory}
            </div>
          </>
        )}
      </div>
      <PaneContent
        onScroll={handleListScroll}
        className={`auto-hide-scrollbar ${isScrollbarActive ? "auto-hide-scrollbar-active" : ""}`}
      >
        {children}
      </PaneContent>
    </PaneFrame>
  );
}
